import { useEffect, useRef, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { isDesktop } from '@/consts/device';
import { getAppStore, setAppStore, useAppStore } from '@/store';
import { generationPipeline } from '@/utils/user/generationPipeline';
import { getProjectJsonUrl } from '@/utils/project/getProjectJsonUrl';
import { removePresetJsonUrlFromUrl } from '@/utils/ui/removePresetJsonUrlFromUrl';
import { submitWebHook } from '@/requests/magic-flow/submitWebHook';
import { getMediaFromFlowId } from '@/requests/magic-flow/getMediaFromFlowId';
import { changeDriveFileName } from '@/requests/cloud-storage/changeDriveFileName';
import { buildParamsFromInputs } from '@/utils/flow/buildParamsFromInputs';
import { createNotificationConfig } from '@/utils/ui/createNotificationConfig';
import { openRegistrationModal } from '@/utils/user/openRegistrationModal';
import { useAuth } from './useAuth';
import { formatTimestamp } from './useGenerate/utils';

import type { IAppStore } from '@/store';
import type { ContentFile } from '@/types';
import { MediaType } from '@/types';

const selector = (state: IAppStore) => ({
  selectedPreset: state.selectedPreset,
  areCreditsAvailable: state.areCreditsAvailable,
  shouldSkipAutoGeneration: state.shouldSkipAutoGeneration,
  inputs: state.inputs,
  toolId: state.toolId,
  folderName: state.folderName,
});

const useAutoGeneration = () => {
  const { isAuthorized } = useAuth();
  const {
    selectedPreset,
    areCreditsAvailable,
    shouldSkipAutoGeneration,
    inputs,
    toolId,
    folderName,
  } = useAppStore(useShallow(selector));

  const previousCreditsRef = useRef<boolean | null>(null);
  const autoGenerationInProgressRef = useRef(false);
  const placeholderUuidRef = useRef<string | null>(null);
  const hasOpenedRegistrationModalRef = useRef(false);
  const { jsonUrl } = getProjectJsonUrl();
  const isDeeplinkSession = Boolean(jsonUrl);
  const [isAutoGenerationPending, setIsAutoGenerationPending] =
    useState(isDeeplinkSession);

  useEffect(() => {
    if (!isDeeplinkSession || shouldSkipAutoGeneration) {
      setIsAutoGenerationPending(false);
      return;
    }

    // Prompt unauthorized users to log in, then auto-generate after login
    if (!isAuthorized) {
      setIsAutoGenerationPending(false);

      if (!hasOpenedRegistrationModalRef.current) {
        hasOpenedRegistrationModalRef.current = true;
        openRegistrationModal();
      }
      return;
    }

    const { isPrimaryActionLoading } = getAppStore();
    const webhookId = selectedPreset?.flowId;

    const creditsBecameAvailable =
      previousCreditsRef.current === false && areCreditsAvailable === true;

    if (areCreditsAvailable !== null) {
      previousCreditsRef.current = areCreditsAvailable;
    }

    const shouldSkipAutoGenerationFlag =
      !webhookId || isPrimaryActionLoading || areCreditsAvailable === false;

    if (
      (shouldSkipAutoGenerationFlag && !creditsBecameAvailable) ||
      autoGenerationInProgressRef.current
    ) {
      setIsAutoGenerationPending(false);
      return;
    }
    const triggerAutoGeneration = async () => {
      // webhookId is guaranteed to exist at this point due to earlier check
      if (!webhookId) return;

      try {
        autoGenerationInProgressRef.current = true;
        setAppStore({
          isPrimaryActionLoading: true,
          isGenerating: true,
          resultFile: null,
          generationProgress: 0,
          generationStartTime: Date.now(),
        });
        setIsAutoGenerationPending(false);

        // Create loading placeholder and navigate to My Creations immediately
        const placeholderUuid = `generating-${Date.now()}`;
        const placeholder: ContentFile = {
          uuid: placeholderUuid,
          name: 'Generating...',
          url: '',
          isLoading: true,
        };
        getAppStore().prependContentFile(placeholder);
        placeholderUuidRef.current = placeholderUuid;

        if (isDesktop) {
          setAppStore({ rightPanelTab: 'creations' });
        } else {
          setAppStore({ screen: 'files' });
        }

        const result = await generationPipeline({ webhookId });

        if (!result?.canProcess) {
          throw new Error('Generation pipeline check failed');
        }

        setAppStore({
          projectCreditsCount: result.projectCreditsCount ?? null,
        });

        // Build params and submit webhook
        const params = buildParamsFromInputs(inputs);
        const webhookResponse = await submitWebHook({
          webhookId,
          params,
          driveOptions: {
            folderName,
            packageId: toolId,
          },
          notificationConfig: createNotificationConfig(),
        });

        // Poll for result
        const flowMedia = await getMediaFromFlowId(webhookResponse.id);
        if (!flowMedia) {
          throw new Error('Failed to get generation result');
        }

        const presetTitle = selectedPreset?.title ?? 'Generated';
        const isVideo = flowMedia.type === MediaType.VIDEO;
        const timestamp = Date.now();
        const extension = isVideo ? 'mp4' : 'png';
        const filename = `${presetTitle} - ${formatTimestamp(
          timestamp
        )}.${extension}`;

        // Rename file on Drive
        if (flowMedia.driveUid) {
          try {
            await changeDriveFileName({
              id: flowMedia.driveUid,
              name: filename,
            });
          } catch {
            // Failed to rename - non-critical
          }
        }

        // Update placeholder with real result
        getAppStore().updateContentFile({
          uuid: placeholderUuid,
          updates: {
            uuid: flowMedia.driveUid || placeholderUuid,
            url: flowMedia.url,
            name: filename,
            type: flowMedia.type,
            preview: {
              ...(!isVideo && { url: flowMedia.url }),
              width: flowMedia.width,
              height: flowMedia.height,
            },
            isLoading: false,
          },
        });

        setAppStore({
          resultFile: flowMedia,
          generationTimestamp: timestamp,
        });

        removePresetJsonUrlFromUrl();
        setAppStore({ shouldSkipAutoGeneration: true });
      } catch (error) {
        console.error('[useAutoGeneration] Auto generation failed:', error);

        // Remove placeholder on error
        if (placeholderUuidRef.current) {
          const { contentFiles, setContentFiles } = getAppStore();
          setContentFiles(
            contentFiles.filter((f) => f.uuid !== placeholderUuidRef.current)
          );
        }

        setAppStore({
          screen: 'create',
          isGenerating: false,
        });
        setIsAutoGenerationPending(false);
        autoGenerationInProgressRef.current = false;
        removePresetJsonUrlFromUrl();
        setAppStore({ shouldSkipAutoGeneration: true });
      } finally {
        setAppStore({
          isPrimaryActionLoading: false,
          isGenerating: false,
          generationStartTime: null,
        });
        placeholderUuidRef.current = null;
      }
    };

    triggerAutoGeneration();
  }, [
    areCreditsAvailable,
    isAuthorized,
    isDeeplinkSession,
    selectedPreset?.flowId,
    selectedPreset?.title,
    inputs,
    toolId,
    folderName,
    shouldSkipAutoGeneration,
  ]);

  return { canAutoGenerate: isAutoGenerationPending };
};

export { useAutoGeneration };
